import _ from 'lodash';
import { Component, OnInit, Input, inject, DestroyRef } from '@angular/core';
import { CampaignService } from '../../services/campaign.service';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as moment from 'moment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { selectCampaignSelected } from 'app/campaign/features/campaign.selectors';

@Component({
  selector: 'app-campaign-auction-settings',
  templateUrl: './campaign-auction-settings.component.html',
  styleUrls: ['./campaign-auction-settings.component.scss']
})
export class CampaignAuctionSettingsComponent implements OnInit {
  destroyRef = inject(DestroyRef)
  @Input() formGroupCampaignTypeData: UntypedFormGroup = null;
  @Input() submitted = false;

  public isAuctionEnded = false;
  
  public auctionTypeList = [{
    id: 1,
    label: 'Silent',
    value: 0
  }, {
    id: 2,
    label: 'Live',
    value: 1
  }];

  public auctionStartTimeRequired: boolean = false;
  public auctionEndTimeRequired: boolean = false;
  public auctionCloseTimeRequired: boolean = false;

  public auctionStartTimeOptions: any = {
    dateFormat: 'mm/dd/yyyy'
  }
  public auctionEndTimeOptions: any = {
    dateFormat: 'mm/dd/yyyy'
  }
  public auctionCloseTimeOptions: any = {
    dateFormat: 'mm/dd/yyyy'
  }

  public auctionEndTimeControl = null;

  public auctionSettingsInstruction:any = null;

  public quillLength:any = 1;
  
  constructor(
    public campaignService: CampaignService,
    private store: Store,
  ) { }

  ngOnInit(): void {
    const auctionType = this.formGroupCampaignTypeData.get('auctionType');
    const auctionStatus = this.formGroupCampaignTypeData.get('auctionStatus');

    const auctionControlMethod = this.formGroupCampaignTypeData.get('auctionControlMethod') as UntypedFormControl;
    const auctionStartTimeControl = this.formGroupCampaignTypeData.get('auctionStartTime') as UntypedFormControl;
    const auctionEndTimeControl = this.formGroupCampaignTypeData.get('auctionEndTime') as UntypedFormControl;
    this.auctionEndTimeControl = auctionEndTimeControl;

    const autoCloseEnabled = this.formGroupCampaignTypeData.get('autoCloseEnabled') as UntypedFormControl;
    const auctionCloseTimeControl = this.formGroupCampaignTypeData.get('auctionCloseTime') as UntypedFormControl;

    const verifyEmailForBidding = this.formGroupCampaignTypeData.get('verifyEmailForBidding') as UntypedFormControl;
    const saveCardRequired = this.formGroupCampaignTypeData.get('saveCardRequired') as UntypedFormControl;

    this.store.pipe(
      select(selectCampaignSelected),
      filter(campaign => campaign !== null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {      
      if(auctionStatus.value === 2) {
        auctionControlMethod.disable()
        autoCloseEnabled.disable()
      } else {
        auctionControlMethod.enable()
        autoCloseEnabled.enable()
      }

      auctionEndTimeControl.setValidators([function(control: AbstractControl) {
        if(auctionControlMethod.value === false) {
          return null;
        }

        const auctionStartTimeControlValue = auctionStartTimeControl.value ? auctionStartTimeControl.value: 0;
        const auctionEndTimeControlValue = control.value ? control.value: 0;
        
        if(auctionEndTimeControlValue === 0) {
          return null;
        }
        
        return auctionStartTimeControlValue > auctionEndTimeControlValue ? { incorrectDate: { value: auctionEndTimeControlValue } } : null;
      }]);

      this.auctionSettingsInstruction = this.formGroupCampaignTypeData.get('auctionWinnerInstruct')

      const { value: { auctionStartTime, auctionEndTime, auctionCloseTime } } = this.formGroupCampaignTypeData;

      auctionType.valueChanges.pipe(
        filter(value => value === 0),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        auctionControlMethod.setValue(false);
        autoCloseEnabled.setValue(false);
      });

      auctionType.valueChanges.pipe(
          filter(value => value === 1),
          takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
          const options = this.formGroupCampaignTypeData.get('presetOptions') as UntypedFormArray;
          _.forEach(options.controls, control => {
            const buyItNowOnlyItem = control.get('buyItNowOnlyItem');
            const buyItNowEnabled = control.get('buyItNowEnabled');
            const buyItNowAmount = control.get('buyItNowAmount') as UntypedFormControl;

            if(buyItNowOnlyItem.value === true) {
              const minBid = control.get('minBid');
              minBid.clearValidators();
              minBid.setValidators([Validators.min(1)])
              minBid.setValue(0);
      
              const bidIncrement = control.get('bidIncrement');
              bidIncrement.clearValidators();
              bidIncrement.setValidators([Validators.min(1)])
              bidIncrement.setValue(0);
            }

            buyItNowOnlyItem.clearValidators();
            buyItNowOnlyItem.setValue(false);

            buyItNowEnabled.clearValidators();
            buyItNowEnabled.setValue(false);

            buyItNowAmount.clearValidators();
            buyItNowAmount.setValue(0);
          });
      });

      auctionControlMethod.valueChanges.pipe(
        filter(value => value === true),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        auctionStartTimeControl.clearValidators();
        auctionStartTimeControl.setValidators([Validators.required, Validators.min(1)]);
        auctionStartTimeControl.updateValueAndValidity();

        this.auctionStartTimeRequired = true;
      });
      auctionControlMethod.valueChanges.pipe(
        filter(value => value === false),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        auctionStartTimeControl.clearValidators();
        auctionStartTimeControl.setValidators([]);
        auctionStartTimeControl.setValue(auctionStartTime);
        auctionStartTimeControl.updateValueAndValidity();

        this.auctionStartTimeRequired = false;
      });

      auctionControlMethod.valueChanges.pipe(
        filter(value => value === true),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        auctionEndTimeControl.setValue(auctionEndTime);
        auctionEndTimeControl.updateValueAndValidity();

        this.auctionEndTimeRequired = false;
      });
      auctionControlMethod.valueChanges.pipe(
        filter(value => value === false),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        auctionEndTimeControl.setValue(auctionEndTime);
        auctionEndTimeControl.updateValueAndValidity();

        this.auctionEndTimeRequired = false;
      });

      auctionControlMethod.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        auctionEndTimeControl.updateValueAndValidity();
      });
      
      autoCloseEnabled.valueChanges.pipe(
        filter(value => value === true),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        auctionCloseTimeControl.clearValidators();
        auctionCloseTimeControl.setValidators([Validators.required, Validators.min(1)]);
        auctionCloseTimeControl.updateValueAndValidity();

        this.auctionCloseTimeRequired = true;
      });
      autoCloseEnabled.valueChanges.pipe(
        filter(value => value === false),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        auctionCloseTimeControl.clearValidators();
        auctionCloseTimeControl.setValidators([]);
        auctionCloseTimeControl.setValue(auctionCloseTime);
        auctionCloseTimeControl.updateValueAndValidity();

        this.auctionCloseTimeRequired = false;
      });

      auctionStartTimeControl.valueChanges.pipe(
        filter(value => !_.isNull(value)),
        filter(value => value > 0),
        debounceTime(100),
        map(value => {
          return moment(value*1000).add(-1, 'days').format('MM/DD/YYYY');
        }),
        map(value => {
          return _.chain(value).split("/").map(element => {
            return parseInt(element);
          }).value();
        }),
        map(value => {
          return {
            year: value[2],
            month: value[0],
            day: value[1]
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((value) => {
        this.auctionEndTimeOptions = {
          ...this.auctionEndTimeOptions,
          disableUntil: value
        };
      });

      auctionStartTimeControl.valueChanges.pipe(
        filter(value => _.isNull(value)),
        debounceTime(100),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((disableUntil) => {
        this.auctionEndTimeOptions = {
          ...this.auctionEndTimeOptions,
          disableUntil
        };
      });

      auctionStartTimeControl.valueChanges.pipe(
        filter(value => value === 0),
        debounceTime(100),
        map(() => null),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((disableUntil) => {
        this.auctionEndTimeOptions = {
          ...this.auctionEndTimeOptions,
          disableUntil
        };
      });

      auctionStartTimeControl.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        auctionEndTimeControl.updateValueAndValidity();
      });

      auctionCloseTimeControl.valueChanges.pipe(
        filter(value => !_.isNull(value)),
        filter(value => value > 0),
        debounceTime(100),
        map(value => {
          return moment(value*1000).add(1, 'days').format('MM/DD/YYYY');
        }),
        map(value => {
          return _.chain(value).split("/").map(element => {
            return parseInt(element);
          }).value();
        }),
        map(value => {
          return {
            year: value[2],
            month: value[0],
            day: value[1]
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((value) => {
        this.auctionCloseTimeOptions = {
          ...this.auctionCloseTimeOptions,
          closeDate: value
        };
      });

      auctionCloseTimeControl.valueChanges.pipe(
        filter(value => _.isNull(value)),
        debounceTime(100),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((closeDate) => {
        this.auctionCloseTimeOptions = {
          ...this.auctionCloseTimeOptions,
          closeDate
        };
      });

      auctionCloseTimeControl.valueChanges.pipe(
        filter(value => value === 0),
        debounceTime(100),
        map(() => null),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((closeDate) => {
        this.auctionCloseTimeOptions = {
          ...this.auctionCloseTimeOptions,
          closeDate
        };
      });

      verifyEmailForBidding.valueChanges.pipe(
        filter(value => !value),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        saveCardRequired.setValue(false, { emitEvent: false });
      });

      saveCardRequired.valueChanges.pipe(
        filter(value => value),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        verifyEmailForBidding.setValue(true, { emitEvent: false });
      });
    });
  }
}
